/**
 * Symbols Tab - Manage slot machine symbols
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Trash2,
  Edit,
  GripVertical,
  Loader2,
  Star,
  Sparkles,
  Target,
  Gem,
  X,
  Upload,
} from 'lucide-react';
import type { SkinSymbolType } from '../../../public/deals/components/slot-machine/themes/skin-types';

/** Symbol type for admin form UI (simplified display) */
type AdminSymbolType = 'wild' | 'scatter' | 'bonus' | 'high' | 'medium' | 'low';

/** Extended symbol interface for admin display (includes joined asset data) */
interface AdminSymbol {
  id: string;
  skinId: string;
  name: string;
  symbolType: SkinSymbolType;
  paytable3?: number;
  paytable4?: number;
  paytable5?: number;
  isExpanding?: boolean;
  paysAnywhere?: boolean;
  winAnimationPreset?: string;
  assetUrl?: string;
  assetFilename?: string;
}

interface SymbolsTabProps {
  skinId: string;
  symbols: AdminSymbol[];
  isLoading: boolean;
}

const SYMBOL_TYPES: { value: AdminSymbolType; label: string; icon: React.ComponentType<{ size?: number }> }[] = [
  { value: 'wild', label: 'Wild', icon: Star },
  { value: 'scatter', label: 'Scatter', icon: Sparkles },
  { value: 'bonus', label: 'Bonus', icon: Target },
  { value: 'high', label: 'High Value', icon: Gem },
  { value: 'medium', label: 'Medium Value', icon: Gem },
  { value: 'low', label: 'Low Value', icon: Gem },
];

interface SymbolFormData {
  name: string;
  symbolType: AdminSymbolType;
  paytable3: number;
  paytable4: number;
  paytable5: number;
  isExpanding: boolean;
  paysAnywhere: boolean;
  winAnimationPreset: string;
}

const DEFAULT_FORM_DATA: SymbolFormData = {
  name: '',
  symbolType: 'high',
  paytable3: 5,
  paytable4: 15,
  paytable5: 50,
  isExpanding: false,
  paysAnywhere: false,
  winAnimationPreset: 'pulse',
};

const WIN_ANIMATION_PRESETS = [
  { value: 'pulse', label: 'Pulse' },
  { value: 'glow', label: 'Glow' },
  { value: 'shake', label: 'Shake' },
  { value: 'bounce', label: 'Bounce' },
  { value: 'sparkle', label: 'Sparkle' },
  { value: 'rotate', label: 'Rotate' },
  { value: 'flip', label: 'Flip' },
  { value: 'zoom', label: 'Zoom' },
];

export default function SymbolsTab({ skinId, symbols, isLoading }: SymbolsTabProps) {
  const queryClient = useQueryClient();
  const [editingSymbol, setEditingSymbol] = useState<AdminSymbol | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState<SymbolFormData>(DEFAULT_FORM_DATA);
  const [uploadingFile, setUploadingFile] = useState<File | null>(null);

  const createMutation = useMutation({
    mutationFn: async (data: SymbolFormData & { file?: File }) => {
      const formDataObj = new FormData();
      formDataObj.append('name', data.name);
      formDataObj.append('symbol_type', data.symbolType);
      formDataObj.append('paytable_3', String(data.paytable3));
      formDataObj.append('paytable_4', String(data.paytable4));
      formDataObj.append('paytable_5', String(data.paytable5));
      formDataObj.append('is_expanding', String(data.isExpanding));
      formDataObj.append('pays_anywhere', String(data.paysAnywhere));
      formDataObj.append('win_animation_preset', data.winAnimationPreset);
      if (data.file) {
        formDataObj.append('file', data.file);
      }

      const response = await fetch(`/api/slot-skins/${skinId}/symbols`, {
        method: 'POST',
        body: formDataObj,
      });
      if (!response.ok) throw new Error('Failed to create symbol');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-slot-skin-symbols', skinId] });
      setShowCreateModal(false);
      setFormData(DEFAULT_FORM_DATA);
      setUploadingFile(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: SymbolFormData & { file?: File } }) => {
      const formDataObj = new FormData();
      formDataObj.append('name', data.name);
      formDataObj.append('symbol_type', data.symbolType);
      formDataObj.append('paytable_3', String(data.paytable3));
      formDataObj.append('paytable_4', String(data.paytable4));
      formDataObj.append('paytable_5', String(data.paytable5));
      formDataObj.append('is_expanding', String(data.isExpanding));
      formDataObj.append('pays_anywhere', String(data.paysAnywhere));
      formDataObj.append('win_animation_preset', data.winAnimationPreset);
      if (data.file) {
        formDataObj.append('file', data.file);
      }

      const response = await fetch(`/api/slot-skins/${skinId}/symbols/${id}`, {
        method: 'PUT',
        body: formDataObj,
      });
      if (!response.ok) throw new Error('Failed to update symbol');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-slot-skin-symbols', skinId] });
      setEditingSymbol(null);
      setFormData(DEFAULT_FORM_DATA);
      setUploadingFile(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/slot-skins/${skinId}/symbols/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete symbol');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-slot-skin-symbols', skinId] });
    },
  });

  // Map database symbol type to admin form type
  const mapToFormType = (type: SkinSymbolType): AdminSymbolType => {
    switch (type) {
      case 'high_value': return 'high';
      case 'medium_value': return 'medium';
      case 'low_value': return 'low';
      case 'jackpot': return 'bonus'; // Map jackpot to bonus for form
      default: return type as AdminSymbolType;
    }
  };

  const handleEdit = (symbol: AdminSymbol) => {
    setEditingSymbol(symbol);
    setFormData({
      name: symbol.name,
      symbolType: mapToFormType(symbol.symbolType),
      paytable3: symbol.paytable3 || 5,
      paytable4: symbol.paytable4 || 15,
      paytable5: symbol.paytable5 || 50,
      isExpanding: symbol.isExpanding || false,
      paysAnywhere: symbol.paysAnywhere || false,
      winAnimationPreset: symbol.winAnimationPreset || 'pulse',
    });
    setUploadingFile(null);
  };

  const handleSave = () => {
    const data = { ...formData, file: uploadingFile || undefined };
    if (editingSymbol) {
      updateMutation.mutate({ id: editingSymbol.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingSymbol(null);
    setFormData(DEFAULT_FORM_DATA);
    setUploadingFile(null);
  };

  const getSymbolIcon = (type: SkinSymbolType) => {
    // Map full types to simplified display types
    const displayType: AdminSymbolType | 'jackpot' = type === 'high_value' ? 'high'
      : type === 'medium_value' ? 'medium'
      : type === 'low_value' ? 'low'
      : type;
    const found = SYMBOL_TYPES.find((t) => t.value === displayType);
    return found?.icon || Gem;
  };

  if (!skinId) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>Save the skin first to manage symbols.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-gray-400" size={32} />
      </div>
    );
  }

  const isModalOpen = showCreateModal || editingSymbol !== null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Symbol Management</h2>
          <p className="text-sm text-gray-500">
            Add and configure slot machine symbols with paytables and animations.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus size={18} />
          Add Symbol
        </button>
      </div>

      {/* Symbols List */}
      {symbols.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <Gem size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No symbols yet. Add your first symbol to get started.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {symbols.map((symbol) => {
            const Icon = getSymbolIcon(symbol.symbolType);
            return (
              <div
                key={symbol.id}
                className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
              >
                <GripVertical size={16} className="text-gray-400 cursor-grab" />

                {/* Symbol Preview */}
                <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                  {symbol.assetUrl ? (
                    <img
                      src={symbol.assetUrl}
                      alt={symbol.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Icon size={24} className="text-gray-400" />
                  )}
                </div>

                {/* Symbol Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{symbol.name}</span>
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full capitalize">
                      {symbol.symbolType}
                    </span>
                    {symbol.isExpanding && (
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-600 text-xs rounded-full">
                        Expanding
                      </span>
                    )}
                    {symbol.paysAnywhere && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-600 text-xs rounded-full">
                        Pays Anywhere
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    Paytable: 3x={symbol.paytable3 || 0}, 4x={symbol.paytable4 || 0}, 5x={symbol.paytable5 || 0}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(symbol)}
                    className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => deleteMutation.mutate(symbol.id)}
                    disabled={deleteMutation.isPending}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold">
                {editingSymbol ? 'Edit Symbol' : 'Add Symbol'}
              </h3>
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Golden 7"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Symbol Type</label>
                <select
                  value={formData.symbolType}
                  onChange={(e) => setFormData({ ...formData, symbolType: e.target.value as AdminSymbolType })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  {SYMBOL_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Paytable */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Paytable (Multipliers)</label>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <span className="text-xs text-gray-500">3 of a kind</span>
                    <input
                      type="number"
                      value={formData.paytable3}
                      onChange={(e) => setFormData({ ...formData, paytable3: Number(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">4 of a kind</span>
                    <input
                      type="number"
                      value={formData.paytable4}
                      onChange={(e) => setFormData({ ...formData, paytable4: Number(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">5 of a kind</span>
                    <input
                      type="number"
                      value={formData.paytable5}
                      onChange={(e) => setFormData({ ...formData, paytable5: Number(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
              </div>

              {/* Special Features */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Special Features</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.isExpanding}
                      onChange={(e) => setFormData({ ...formData, isExpanding: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">Expanding (fills entire reel on win)</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.paysAnywhere}
                      onChange={(e) => setFormData({ ...formData, paysAnywhere: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">Pays Anywhere (scatter-style wins)</span>
                  </label>
                </div>
              </div>

              {/* Win Animation */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Win Animation</label>
                <select
                  value={formData.winAnimationPreset}
                  onChange={(e) => setFormData({ ...formData, winAnimationPreset: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  {WIN_ANIMATION_PRESETS.map((preset) => (
                    <option key={preset.value} value={preset.value}>
                      {preset.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Asset Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Symbol Image/Model</label>
                <label className="block">
                  <input
                    type="file"
                    accept="image/*,.glb,.gltf"
                    onChange={(e) => setUploadingFile(e.target.files?.[0] || null)}
                    className="hidden"
                  />
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-colors">
                    {uploadingFile ? (
                      <span className="text-sm text-gray-600">{uploadingFile.name}</span>
                    ) : editingSymbol?.assetUrl ? (
                      <span className="text-sm text-gray-600">Current: {editingSymbol.assetFilename || 'Uploaded'}</span>
                    ) : (
                      <>
                        <Upload size={24} className="mx-auto text-gray-400 mb-2" />
                        <span className="text-sm text-gray-500">Click to upload image or 3D model</span>
                      </>
                    )}
                  </div>
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!formData.name || createMutation.isPending || updateMutation.isPending}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {(createMutation.isPending || updateMutation.isPending) && (
                  <Loader2 size={16} className="animate-spin" />
                )}
                {editingSymbol ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
